import {
  AddIcon,
  ButtonThemes,
  CheckCircleIcon,
  Col,
  IconButton,
  Modal,
  ModalSizes,
  Row,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { ReactComponent as DecreaseIcon } from './decreaseIcon.svg';

import styles from './styles.module.scss';

import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import { Queries } from 'library/queries';
import translation from 'locales/translation';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useSaveNewVote, VoteOption } from 'services/editVotes';

export enum EventType {
  AGE = 'Assembleia Geral Extraordinária',
  AGO = 'Assembleia Geral Ordinária',
  AOE = 'Assembleia Geral Ordinária e Extraordinária',
}

interface EditProps extends ModalProps {
  agendaId: number;
  eventId: number;
  description: string;
  branch: string;
  nucleou: string;
  ldap: string;
  type: EventType;
  associateElegible: number;
  associatePresent: number;
  options: Omit<VoteOption, 'totalVotes' | 'votesCount' | 'options'>[];
  onSuccess?: () => void;
}

export const AgnVotesEditor = (props: EditProps) => {
  const initialCounts = props.options.map((e) => e.votes ?? 0);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState<number[]>(initialCounts);

  const singleVote = useMemo(() => {
    return [EventType.AGE, EventType.AGO, EventType.AOE].includes(props.type);
  }, [props.type]);

  const isAltered = JSON.stringify(counts) !== JSON.stringify(initialCounts);

  const { showToast } = useContext(LayoutContext);
  const { mutate, isPending } = useSaveNewVote();
  const queryClient = useQueryClient();

  const handleClose = () => {
    props.onClose && props.onClose(props.modalKey);
  };

  const totalVotes = useMemo(
    () => counts.reduce((acc, count) => acc + count, 0),
    [counts]
  );

  const handleIncrement = useCallback(
    (index: number) => {
      if (singleVote) {
        setCounts((prevCounts) =>
          prevCounts.map((_, i) => (i === index ? 1 : 0))
        );
      } else if (totalVotes < props.associatePresent) {
        setCounts((prevCounts) =>
          prevCounts.map((count, i) => (i === index ? count + 1 : count))
        );
      }
    },
    [singleVote, totalVotes, props.associatePresent]
  );

  const handleDecrement = useCallback((index: number) => {
    setCounts((prevCounts) =>
      prevCounts.map((count, i) =>
        i === index && count > 0 ? count - 1 : count
      )
    );
  }, []);

  const handleInputChange = useCallback((index: number, value: string) => {
    const newValue = value === '' ? 0 : parseInt(value, 10);
    if (!isNaN(newValue) && newValue >= 0) {
      setCounts((prevCounts) =>
        prevCounts.map((count, i) => (i === index ? newValue : count))
      );
    }
  }, []);

  return (
    <Modal
      title={'Editor de votos'}
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      onClose={props.onClose}
      onSubmit={props.onSubmit}
      size={ModalSizes.Large}
      className={styles['edit-root']}
      footer={[
        {
          theme: ButtonThemes.Secondary,
          leftIcon: <TimesCircleIcon />,
          children: 'Cancelar',
          onClick: () => props.onClose!(props.modalKey),
        },
        {
          theme: ButtonThemes.Primary,
          leftIcon: <CheckCircleIcon />,
          children: `Atualizar voto${props.associateElegible > 1 ? 's' : ''}`,
          loading: isPending || loading,
          disabled:
            isPending || loading || !isAltered || singleVote
              ? totalVotes > 1 || totalVotes <= 0 || !isAltered
              : totalVotes !== props.associateElegible,
          onClick: () => {
            setLoading(true);
            mutate(
              {
                agendaId: props.agendaId,
                branch: props.branch,
                nucleus: props.nucleou,
                flagNoVotes: false,
                ldap: props.ldap,
                items: props.options.map((item, index: number) => {
                  return {
                    itemId: item.itemId,
                    quantity: counts[index],
                    flagAcclamation: false,
                  };
                }),
                eventId: props.eventId,
              },
              {
                onSuccess: () => {
                  queryClient
                    .refetchQueries({
                      queryKey: [Queries.GetVoteEditInfos],
                    })
                    .finally(() => {
                      setLoading(false);
                      handleClose();
                    });
                },
                onError: (error: Error) => {
                  setLoading(false);
                  showToast({
                    pauseOnFocusLoss: true,
                    theme: ToastTypes.Error,
                    showStatusBar: true,
                    timeout: 4000,
                    label:
                      translation.Toasts.Errors.Keys[
                        `${
                          (error as AxiosError<Error>).response?.data.message ||
                          (error as AxiosError<Error>).response?.data ||
                          (error as AxiosError<Error>).message
                        }` as keyof typeof translation.Toasts.Errors.Keys
                      ] ?? translation.Toasts.Errors.defaultAlternative,
                  });
                },
              }
            );
          },
        },
      ]}
    >
      <Row>
        <Row>
          <Col>
            <p>{translation.Assessor.Modal.AgnVotesEditor.warning}</p>
          </Col>
          <Row>
            <Col cols={[6, 6, 6, 6, 6]}>
              <Row>
                <Col noPaddingTop className={styles['no-padding']}>
                  <p className={styles.title}>{props.branch}</p>
                </Col>
                <Col noPaddingTop className={styles['no-padding']}>
                  <span>
                    {translation.Assessor.Modal.AgnVotesEditor.branch}
                  </span>
                </Col>
              </Row>
            </Col>
            <Col cols={[6, 6, 6, 6, 6]}>
              <Row>
                <Col noPaddingTop className={styles['no-padding']}>
                  <p className={styles.title}>{props.nucleou}</p>
                </Col>
                <Col noPaddingTop className={styles['no-padding']}>
                  <span>
                    {translation.Assessor.Modal.AgnVotesEditor.nucleous}
                  </span>
                </Col>
              </Row>
            </Col>
          </Row>
        </Row>
        <Row>
          <Col>
            <Row>
              <Col noPaddingTop className={styles['no-padding']}>
                <h1>{translation.Assessor.Modal.AgnVotesEditor.description}</h1>
              </Col>
              <Col noPaddingTop className={styles['no-padding']}>
                <p>{props.description}</p>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col>
            <h1>{translation.Assessor.Modal.AgnVotesEditor.voteOption}</h1>
          </Col>
          <Col centralized>
            <p>
              Associados presentes:&nbsp;
              {props.associatePresent ?? '0'}
              &nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;Associados aptos:&nbsp;
              {props.associateElegible ?? '0'}
            </p>
          </Col>
          <Row>
            {props.options.map((option, index) => (
              <Col key={option.description} style={{ width: '100%' }}>
                <Row>
                  <p>{option.description}</p>
                </Row>
                <Row className={styles.count}>
                  <IconButton
                    icon={<DecreaseIcon />}
                    onClick={() => handleDecrement(index)}
                    disabled={isPending || loading || counts[index] <= 0}
                  />
                  <input
                    type={'text'}
                    value={counts[index]}
                    onChange={(e) => handleInputChange(index, e.target.value)}
                    className={styles.input}
                    inputMode={'numeric'}
                    disabled={isPending || loading || counts[index] <= 0}
                    onBeforeInput={(e: any) => {
                      if (!/[0-9]/.test(e.data || '')) {
                        e.preventDefault();
                      }
                    }}
                  />
                  <IconButton
                    icon={<AddIcon />}
                    onClick={() => handleIncrement(index)}
                    disabled={
                      isPending || loading || singleVote
                        ? totalVotes >= 1
                        : totalVotes >= props.associateElegible
                    }
                  />
                </Row>
              </Col>
            ))}
          </Row>
        </Row>
      </Row>
    </Modal>
  );
};
